import { luscinia } from "../luscinia";
import { constructBootgridFormatters } from "./bootgridFormatters";
import ParameterBootgridManager from "./parameter-bootgrid-manager";
import { BootgridSettings } from "./bootgridSettings";
import { BootgridChangeType } from "./bootgridTypes";
import { postValueChange } from "../wizard";

export default class ParameterBootgrid {
    private readonly config: Config;
    private readonly bootgridSettings: BootgridSettings;
    private readonly $bootgrid: JQuery<HTMLElement>;
    private readonly name: string;
    private readonly bootgridManager: ParameterBootgridManager;

    constructor(config: Config, tableElement: HTMLTableElement) {
        this.config = config;
        this.name = tableElement.dataset.lusciniaTablename;
        this.$bootgrid = $(tableElement);
        this.bootgridSettings = new BootgridSettings(this.$bootgrid, config);
        if (!this.bootgridSettings.isParameterBootgrid) {
            throw new Error("This is not a parameter bootgrid.")
        }
        this.bootgridManager = new ParameterBootgridManager(this.$bootgrid);
    }

    init() {
        (this.$bootgrid as any)
            .on("loaded.rs.jquery.bootgrid", () => {
                const editBtns = this.$bootgrid.find(".command-edit");
                editBtns.each((index, element) => {
                    $(element).on("click", (e) => {
                        e.stopPropagation();
                        const rowId = $(e.currentTarget).closest("tr").data("row-id");
                        luscinia.openCustomModalEdit(`/data/${this.name}`, rowId, "");
                    });
                });
                this.initInlineEditable();
            })
            .on("selected.rs.jquery.bootgrid", (e: Event, rows: object[]) => {
                if (this.hasPostValueChange()) {
                    const additionalFormData = this.bootgridSettings.allowMultiSelect
                        ? this.bootgridManager.getPostValueChangeFormData(rows, BootgridChangeType.select, null)
                        : null;
                    this.postValueChange(additionalFormData);
                }
            })
            .on("deselected.rs.jquery.bootgrid", (e: Event, rows: object[]) => {
                if (this.hasPostValueChange()) {
                    const additionalFormData = this.bootgridSettings.allowMultiSelect
                        ? this.bootgridManager.getPostValueChangeFormData(rows, BootgridChangeType.deselect, null)
                        : null;
                    this.postValueChange(additionalFormData);
                }
            })
            .bootgrid({
                rowCount: -1,
                navigation: 0,
                formatters: constructBootgridFormatters(this.$bootgrid, this.bootgridSettings, this.config),
                // Forward the server's selection settings. Without these, jquery.bootgrid falls back
                // to its gridDefaults (selection/multiSelect/rowSelect/keepSelection all false), so a
                // GridMultiSelect parameter table rendered no checkbox column and nothing could be
                // selected - leaving SelectedRows permanently empty and the wizard unable to do any
                // work. The selected/deselected handlers above have always assumed otherwise.
                // Grids that opt out (AllowSelection false) keep behaving exactly as before.
                selection: this.bootgridSettings.allowSelection,
                multiSelect: this.bootgridSettings.allowMultiSelect,
                keepSelection: this.bootgridSettings.allowMultiSelect,
                rowSelect: this.bootgridSettings.allowRowSelection,
            });
    }
    private initInlineEditable() {
        var inputList = document.querySelectorAll('input.inline-editable') as NodeListOf<HTMLInputElement>;
        inputList.forEach(input => {
            input.addEventListener('change', () => {
                $(input).attr('value', input.value);
                let rows = (this.$bootgrid as any).bootgrid('getCurrentRows') as object[];
                const rowId = $(input).closest("tr").attr('data-row-id');
                const keyColumnName = this.$bootgrid.attr('data-luscinia-keycolumnname');
                let changedRow = rows.filter(row => row[keyColumnName] == rowId);
                let changedRowIndex = rows.findIndex(row => row[keyColumnName] == rowId);
                const changedPropertyName = $(input).attr('name');
                if (input.value != "") {
                    changedRow[0][changedPropertyName] = input.value;
                } else {
                    changedRow[0][changedPropertyName] = "0";
                }
                rows[changedRowIndex] = changedRow[0];
                const additionalFormData = this.bootgridManager.getPostValueChangeFormData(changedRow, BootgridChangeType.update, changedPropertyName);
                this.postValueChange(additionalFormData);
            });
        });
    }
    private hasPostValueChange() {
        return this.$bootgrid.attr("data-luscinia-bootgrid-postvaluechange") === "true";
    }

    private postValueChange(additionalData: FormData = null) {
        let wizardForm = $(".block-wizard").find("form") as JQuery<HTMLFormElement>;
        let parameterName = $(".block-wizard").data("parameter");
        postValueChange.call(this.$bootgrid[0], wizardForm, parameterName, this.$bootgrid.attr("data-luscinia-field-name"), additionalData);
    }
}
